import React, { useEffect, useMemo, useRef, useState, useContext } from "react";

// Third-parties.

import * as dayjs from 'dayjs';

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import { CSVLink } from 'react-csv';

// Helper.
import Helper from '../../../helpers/Helper';

// Types.
import SessionsDataTable from '../../../types/instance/tables/SessionsDataTable';
import { FilterType } from '../../../typescript/Enums';

// Components.
import Alert from "../../Alert";
import GenericBreakdown from '../../GenericBreakdown';
import NoData from '../../NoData';
import TablePagination from '../../TablePagination';

// Constants.
import { getDefaultPageSize } from "../../../helpers/utils";
import { useLocation, useNavigate } from "react-router-dom";

// Contexts.
import { FilterContext } from '../../../context/FilterContext';

function SessionsTable(props: {
    instanceId: number,
    batchId?: string,
    statementId?: string,
    instanceTime: number,
    sessionsDataTable: SessionsDataTable[],
    setFilterOptions: Function,
    currentTab?: string,
    tabIdPrefix?: string
}) {
    const tabIdPrefix = props.tabIdPrefix || '';
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(tabIdPrefix ? "" : (query.get("filter") || ""));
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const { instanceTime, setFilterOptions } = props;
    
    const { currentFilterOptions } = useContext(FilterContext);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    useEffect(() => {
        // Clear Filter if tab is changed
        if (props.currentTab) {
            setTableFilter('')
        }
    }, [props.currentTab]);

    const data = useMemo(() => props.sessionsDataTable.filter(row => (row.session.toLowerCase().includes(tableFilter.toLowerCase()))), [props.sessionsDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => {
        function filterData(filter: string): void {
            setFilterOptions(Helper.updateFilterOptions(FilterType.Sessions,
                    filter, currentFilterOptions));
        }

        return [
            {
                accessor: 'color',
                disableSortBy: true
            },
            {
                accessor: 'id',
                disableSortBy: true
            },
            {
                Header: 'Session',
                accessor: 'session',
                className: 'fw-bold',
                defaultCanSort: true,
                sortType: 'alphanumeric',
                Cell: ({ cell }) => (
                        <React.Fragment>
                            <span className="me-1 ps-1" style={{ backgroundColor: cell.row.values.color }}>&nbsp;</span>
                            {(cell.row.values.session !== '-') ? (
                                    <button key={cell.column.id} className="btn btn-sm btn-link btn-filter"
                                            onClick={() => filterData(cell.row.values.session)}>{cell.row.values.session}</button>
                            ) : (
                                    <React.Fragment>-</React.Fragment>
                            )}
                        </React.Fragment>
                ),
            },
            {
                Header: 'Total Time',
                accessor: 'breakdown',
                className: 'generic-waits text-nowrap',
                disableSortBy: true,
                Cell: ({ cell }) => (
                        cell.row.values.waittime !== null ? (
                                cell.row.values.id < 20 ? (
                                        <GenericBreakdown individualTime={cell.row.values.waittime} totalTime={instanceTime}
                                                          color={((cell.row.values.color !== '') ? cell.row.values.color : '#999')}
                                                          tooltip={cell.row.values.session}/>
                                ) : (
                                        <React.Fragment>-</React.Fragment>
                                )
                        ) : (
                                <React.Fragment>-</React.Fragment>
                        )
                )
            },
            {
                Header: 'Wait Time',
                accessor: 'waittime',
                headerClassName: 'text-end',
                className: 'text-end',
                defaultCanSort: true,

                sortType: (rowA, rowB, columnId) => {
                    const aWait = Number(rowA.values[columnId]) || 0;
                    const bWait = Number(rowB.values[columnId]) || 0;

                    if (aWait !== bWait) {
                        return bWait - aWait;
                    }

                    const aHash = Number(rowA.values['session']) || 0;
                    const bHash = Number(rowB.values['session']) || 0;
                    return aHash - bHash;
                },
                Cell: ({ cell }) => (
                        cell.row.values.waittime !== null ? (
                                Helper.getTimeInEnglish(cell.row.values.waittime)
                        ) : (
                                <React.Fragment>-</React.Fragment>
                        )
                )
            },
            {
                Header: 'Weight %',
                accessor: 'weight',
                headerClassName: 'text-end',
                className: 'text-end',
                defaultCanSort: true,
                sortType: 'basic',
                Cell: ({ cell }) => {
                    const { waittime } = cell.row.values;
                    const percentage = (100 / instanceTime) * waittime;

                    return (
                            <React.Fragment>
                                {instanceTime ? `${Helper.formatPercentage(percentage)}%` : '-'}
                            </React.Fragment>
                    );
                }
            }
        ]
    }, [instanceTime, currentFilterOptions, setFilterOptions]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
                columns,
                data,
                initialState: {
                    hiddenColumns: ['id', 'color'],
                    pageIndex: initialPageIndex,
                    pageSize: initialPageSize,
                }
            },
            useSortBy, usePagination);

    useEffect(() => {
        // Don't update URL params when in panel mode (tabIdPrefix is set)
        if (tabIdPrefix) return;
        
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize, tabIdPrefix]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
            <div id={`${tabIdPrefix}sessions`} className="tab-pane fade show" role="tabpanel" aria-labelledby={`${tabIdPrefix}sessions-tab`}>
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-user-clock fa-fw" aria-hidden="true"></i>
                        Sessions
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseSessions" aria-expanded="false" aria-controls="collapseSessions"></i>
                    </div>
                    <div id="collapseSessions" className="card-body collapse show">
                        {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 && (
                                <Alert
                                        message={`To help preserve performance, we limit the total number of session records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their total wait time.`}
                                        heading="Session records" variant="alert-info"/>
                        )}
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                                <CSVLink role="button"
                                         headers={[{ label: 'ID', key: 'id' }, {
                                             label: 'Session',
                                             key: 'session'
                                         }, { label: 'Wait Time', key: 'waittime' }]}
                                         data={data}
                                         download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Sessions.csv`}
                                         className="btn btn-sm btn-primary">
                                    <i className="fal fa-file-export fa-fw"></i><span>Export</span>
                                </CSVLink>
                                <button className="btn btn-sm btn-dark" onClick={clearTableFilter}><i
                                        className="far fa-undo"></i><span>Clear</span></button>
                            </div>
                            <div className="col col-md-2">
                                <input type="text" ref={inputRef} className="form-control form-control-sm" placeholder="Search"
                                       value={tableFilter} data-lpignore={true}
                                       onChange={(e) => setTableFilter(e.target.value)}/>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table {...getTableProps()} className="table">
                                <thead>
                                {headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map(column => (
                                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                        className={(column as any).className}>
                                                        {column.render('Header')}
                                                        {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                                        <i className="fal fa-sort-amount-up-alt"></i> :
                                                                        <i className="fal fa-sort-amount-down-alt"></i>) :
                                                                <i className="fal fa-sort-amount-down-alt text-light"></i>) : ''}
                                                    </th>
                                            ))}
                                        </tr>
                                ))}
                                </thead>
                                <tbody {...getTableBodyProps()}>
                                {page.map(row => {
                                    prepareRow(row)
                                    return (
                                            <tr {...row.getRowProps()}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                            <td {...cell.getCellProps()} className={(cell.column as any).className}
                                                                style={{ borderLeft: (cell.column as any).color }}>
                                                                {cell.render('Cell')}
                                                            </td>
                                                    )
                                                })}
                                            </tr>
                                    )
                                })}
                                </tbody>
                            </table>
                            <NoData
                                    error={null}
                                    loading={false}
                                    length={data.length}
                            />
                            <TablePagination
                                    length={data.length}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    canPreviousPage={canPreviousPage}
                                    previousPage={previousPage}
                                    canNextPage={canNextPage}
                                    nextPage={nextPage}
                                    gotoPage={gotoPage}
                                    pageIndex={pageIndex}
                            />
                        </div>
                    </div>
                </div>
            </div>
    );
}

export default SessionsTable;
